import type { CoreApplicationContext } from '@eventbrite/legacy-infra.context-types';
import { getNarrowMenuInfo } from '@eventbrite/legacy-eds.global-header';
import { Icon } from '@eventbrite/legacy-eds.icon';
import { PlusChunky } from '@eventbrite/legacy-eds.iconography';
import classNames from 'classnames';
import { gettext } from '@eventbrite/legacy-infra.i18n';
import {
    getCreateEventCTA,
    getHelpMenu,
    getNonUserQuickLinks,
    getOrganizeMenu,
    getUserMenuInfo,
    logCreateEventCTAClick,
} from '@eventbrite/legacy-infra.site-header';
import React, { useState } from 'react';
import type { FeatureFlags, UserShape } from '@eventbrite/consumer-header.global-nav/types';
import { CreateEventAlternatives } from '../CreateEventAlternatives';
import { Modal } from '@eventbrite/marmalade';
import { getLikesQuickLinks, getMyTicketsQuickLinks } from './core-js-global-copies';
import { GlobalHeaderMenu, GlobalHeaderQuickLinks } from '../GlobalHeaderNavigationBar';
import styles from './ConsumerHeaderLinks.module.scss';

interface ConsumerHeaderLinksProps {
    user: UserShape;
    env: CoreApplicationContext['env'] & {
        launchNewSignInSignUpLinks?: boolean;
        isManageOverviewActive?: boolean;
    };
    featureFlags?: FeatureFlags;
    requestPath?: string;
    isHomePage?: boolean;
    onAdditionalMenuTextItemSelect?: Function;
    onClickDropdown?: Function;
}

const _showCreateEventCTA = ({ canCreateEvents }: UserShape, isHomePage?: boolean) => {
    // Only show the Create Event Cta if the user can create events, or is on the homepage
    return canCreateEvents || isHomePage;
};

export const ConsumerHeaderMobileLinks: React.FC<ConsumerHeaderLinksProps> = ({
    user,
    env,
    featureFlags,
    requestPath,
    isHomePage,
    onClickDropdown,
}) => {
    // Users only see the ConsumerHeader if they are authenticated. Else they see the GlobalNav
    const { isAuthenticated } = user;
    const {
        adminServerUrl,
        isManageOverviewActive,
        logoutUrl,
        serverUrl,
        isMobile,
        organizeUrls,
        helpUrls,
        signinUrl,
        signupUrl,
        loginUrl,
        launchNewSignInSignUpLinks,
        ebDomain,
    } = env;

    const additionalMenus = [
        //getOrganizeMenu(organizeUrls, requestPath)
        getOrganizeMenu(
            { home: organizeUrls.home, suggested: organizeUrls.suggested },
            requestPath,
        ),
        getHelpMenu(helpUrls, requestPath),
    ];

    const userMenuInfo = getUserMenuInfo({
        adminServerUrl,
        featureFlags: {
            ...featureFlags,
            isDetachInterestsEnabled: false,
        },
        isManageOverviewActive,
        logoutUrl,
        requestPath,
        serverUrl,
        showBrowseEvents: true,
        user,
        isMobile,
    });

    const signinPath = launchNewSignInSignUpLinks ? signinUrl : loginUrl;
    const nonUserQuickLinks = getNonUserQuickLinks(
        requestPath,
        isAuthenticated,
        signinPath,
        signupUrl,
        { launchNewSignInSignUpLinks },
    );

    const showCreateEventCTA = _showCreateEventCTA(user, isHomePage);

    const callToActionInfo = getCreateEventCTA(
        serverUrl,
        ebDomain,
        requestPath,
        isAuthenticated,
        false, //was null originally
        { showCreateEventCTA },
        () => logCreateEventCTAClick(isHomePage),
    );

    const narrowMenuInfo = getNarrowMenuInfo(
        userMenuInfo,
        additionalMenus,
        [],
        callToActionInfo,
        [],
        true,
    );

    const narrowMenu = [narrowMenuInfo].map((menuInfo, index) => (
        <span key={menuInfo.path + index}>
            <GlobalHeaderMenu
                menuInfo={menuInfo}
                data-testid="global-header-narrow-menu"
                noLabel={true}
                onClickDropdown={onClickDropdown}
            />
        </span>
    ));

    const { publicId } = user;

    const likesLinks = getLikesQuickLinks(serverUrl, ebDomain, requestPath || '', publicId);

    const myTicketsLinks = getMyTicketsQuickLinks(serverUrl, ebDomain, requestPath || '', publicId);

    return (
        <div className={styles.mobileLinks} data-testid="consumer-header-mobile-links">
            <div className={styles.links}>
                <div className={styles.likesAndTicketsContainer}>
                    <GlobalHeaderQuickLinks
                        key="likesLinks"
                        links={likesLinks}
                        shouldShowIcon={true}
                    />
                    <GlobalHeaderQuickLinks
                        key="myTicketsLinks"
                        links={myTicketsLinks}
                        shouldShowIcon={true}
                    />
                </div>
                {narrowMenu}
                <GlobalHeaderQuickLinks links={nonUserQuickLinks} shouldShowIcon={false} />
            </div>
        </div>
    );
};

export const ConsumerHeaderDesktopLinks: React.FC<ConsumerHeaderLinksProps> = ({
    user,
    env,
    // featureFlags,
    requestPath,
    // isHomePage,
}) => {
    const { ebDomain, serverUrl } = env;
    const { publicId } = user;
    const [shouldShowDialog, setShouldShowDialog] = useState(false);
    const toggleModal = () => setShouldShowDialog((isShown: boolean) => !isShown);

    const likesLinks = getLikesQuickLinks(serverUrl, ebDomain, requestPath || '', publicId);

    const myTicketsLinks = getMyTicketsQuickLinks(serverUrl, ebDomain, requestPath || '', publicId);

    const ctaIcon = <Icon size="small" type={<PlusChunky />} />;

    let linkComponents: any[] = [];

    linkComponents = [
        ...linkComponents,
        //Migrating the auto_create_event_for_existing_creators experiment branch as is the default currently
        <React.Fragment key="autoCreateEventWithAIModal">
            <a
                onClick={toggleModal}
                data-automation="global-nav-create"
                data-testid="consumer-header-ai-create-event-button"
                className={classNames(styles.quickLink, styles.menuLabel, styles.createEventLink)}
                style={{ cursor: 'pointer' }}
            >
                {ctaIcon}
                {gettext('Create an event')}
            </a>
            <Modal
                isShown={shouldShowDialog}
                handleClose={toggleModal}
                closeTitle={gettext('Close')}
                headerTitle={gettext('How do you want to build your event?')}
                className={styles.createEventModal}
                children={<CreateEventAlternatives />}
            />
        </React.Fragment>,
        <React.Fragment key="createEventClassicFlow">
            <GlobalHeaderQuickLinks key="likesLinks" links={likesLinks} shouldShowIcon={true} />
            <GlobalHeaderQuickLinks
                key="myTicketsLinks"
                links={myTicketsLinks}
                shouldShowIcon={true}
            />
        </React.Fragment>,
    ];

    return (
        <div className={styles.links} data-testid="consumer-header-desktop-links">
            {linkComponents}
        </div>
    );
};
